import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import Button from './Button';
import {
  type FacebookPage,
  type InstagramBusinessAccount,
  getAvailablePages,
  finalizeConnection,
} from '../lib/api';

interface FacebookPageSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  connectionId: string;
  token: string;
  platform: 'facebook_page' | 'instagram_business';
  onSuccess: () => void;
}

export default function FacebookPageSelectorModal({
  isOpen,
  onClose,
  connectionId,
  token,
  platform,
  onSuccess,
}: FacebookPageSelectorModalProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [error, setError] = useState<string>('');
  const [pages, setPages] = useState<FacebookPage[]>([]);
  const [instagramAccounts, setInstagramAccounts] = useState<InstagramBusinessAccount[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string>('');
  const [selectedInstagramId, setSelectedInstagramId] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadAvailablePages();
    }
  }, [isOpen, connectionId, token]);

  const loadAvailablePages = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await getAvailablePages(connectionId, token);
      setPages(response.pages || []);
      setInstagramAccounts(response.instagramAccounts || []);

      // Auto-select first page/account if only one available
      if (response.pages.length === 1) {
        setSelectedPageId(response.pages[0].id);
      }
      if (response.instagramAccounts && response.instagramAccounts.length === 1) {
        setSelectedInstagramId(response.instagramAccounts[0].id);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('api.connection.errors.failedToLoadPages'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalize = async () => {
    if (!selectedPageId) {
      setError(t('api.connection.errors.selectPage'));
      return;
    }

    if (platform === 'instagram_business' && !selectedInstagramId) {
      setError(t('api.connection.errors.selectInstagramAccount'));
      return;
    }

    setIsFinalizing(true);
    setError('');

    try {
      await finalizeConnection(connectionId, {
        pageId: selectedPageId,
        instagramAccountId: platform === 'instagram_business' ? selectedInstagramId : undefined,
        token,
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || t('api.connection.errors.failedToFinalize'));
    } finally {
      setIsFinalizing(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('api.connection.selectPage.title')}
      size="lg"
    >
      <div className="space-y-6">
        {/* Instructions */}
        <p className="text-gray-700 dark:text-gray-300">
          {platform === 'instagram_business'
            ? t('api.connection.selectPage.instagramInstructions')
            : t('api.connection.selectPage.facebookInstructions')}
        </p>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-400 p-4">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-black dark:border-gray-600 dark:border-t-white"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">
              {t('api.connection.selectPage.loading')}
            </p>
          </div>
        )}

        {/* Facebook Pages */}
        {!isLoading && pages.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {t('api.connection.selectPage.selectFacebookPage')}
            </h3>

            <div className="space-y-2">
              {pages.map((page) => (
                <label
                  key={page.id}
                  className={`
                    block p-4 border-2 border-black dark:border-gray-700 cursor-pointer transition-colors
                    ${selectedPageId === page.id ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-950 hover:bg-gray-50 dark:hover:bg-gray-900'}
                  `}
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="page"
                      value={page.id}
                      checked={selectedPageId === page.id}
                      onChange={(e) => setSelectedPageId(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 dark:text-white">{page.name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{page.category}</p>
                      {page.tasks && page.tasks.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {t('api.connection.selectPage.permissions')}: {page.tasks.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Instagram Accounts (only for Instagram Business) */}
        {!isLoading && platform === 'instagram_business' && instagramAccounts.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {t('api.connection.selectPage.selectInstagramAccount')}
            </h3>

            <div className="space-y-2">
              {instagramAccounts.map((account) => (
                <label
                  key={account.id}
                  className={`
                    block p-4 border-2 border-black dark:border-gray-700 cursor-pointer transition-colors
                    ${selectedInstagramId === account.id ? 'bg-pink-50 dark:bg-pink-900/20' : 'bg-white dark:bg-gray-950 hover:bg-gray-50 dark:hover:bg-gray-900'}
                  `}
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="instagram"
                      value={account.id}
                      checked={selectedInstagramId === account.id}
                      onChange={(e) => setSelectedInstagramId(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 dark:text-white">@{account.username}</p>
                      {account.name && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">{account.name}</p>
                      )}
                      {account.followers_count !== undefined && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {account.followers_count.toLocaleString()} {t('api.connection.selectPage.followers', { count: account.followers_count })}
                        </p>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* No Pages Found */}
        {!isLoading && pages.length === 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              {t('api.connection.selectPage.noPagesFound')}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-3 pt-4 border-t-2 border-black dark:border-gray-700">
          <Button
            onClick={onClose}
            variant="secondary"
            disabled={isFinalizing}
            className="flex-1"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleFinalize}
            disabled={
              isFinalizing ||
              !selectedPageId ||
              (platform === 'instagram_business' && !selectedInstagramId)
            }
            className="flex-1"
          >
            {isFinalizing ? t('api.connection.selectPage.finalizing') : t('api.connection.selectPage.finalize')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
